import { queryOptions } from '@tanstack/react-query';
import type {
  ExternalGetBillerLocationTokenParams,
  ExternalGetPricingInfoParams,
  ExternalListTrialsParams,
  ExternalGetPlanGroupParams,
  ExternalGetSquareOneSubscriptionInfoParams,
  ExternalGetUiAssetsParams,
  ExternalCreateOrUpdateSubscriptionParams,
  ExternalUpdateSubscriptionParams,
  ExternalSetSquareOneMigrationDecisionParams,
  ExternalGetSquareOneLostFeaturesParams,
  ExternalMigrateToSquareOneParams,
  ExternalGetTierRecommendationParams,
  ExternalGetSquareOneMigrationFeaturesParams,
} from '../api/subscriptions/types.js';
import { featurama } from './featurama';
import { feePlans } from './feePlans.js';
import { saasBff } from './saas-bff.js';

export const subscriptions = {
  keys: {
    root: () => ['subscriptions'],
    mergedSubscriptions: () => [...subscriptions.keys.root(), 'externalListMergedSubscriptions'],
    featureStatuses: () => [...subscriptions.keys.root(), 'externalListFeatureStatuses'],
    billerLocationToken: () => [...subscriptions.keys.root(), 'externalGetBillerLocationToken'],
    squareOneEligibility: () => [...subscriptions.keys.root(), 'externalGetSquareOneEligibility'],
    pricingInfo: () => [...subscriptions.keys.root(), 'externalGetPricingInfo'],
    trials: () => [...subscriptions.keys.root(), 'externalListTrials'],
    planGroup: () => [...subscriptions.keys.root(), 'externalGetPlanGroup'],
    squareOneSubscriptionInfo: () => [...subscriptions.keys.root(), 'externalGetSquareOneSubscriptionInfo'],
    squareOneMigrationFeatures: () => [...subscriptions.keys.root(), 'externalGetSquareOneMigrationFeatures'],
    uiAssets: () => [...subscriptions.keys.root(), 'externalGetUiAssets'],
    squareOneLostFeatures: () => [...subscriptions.keys.root(), 'externalGetSquareOneLostFeatures'],
    squareOneMigrationDataTemporary: () => [...subscriptions.keys.root(), 'squareOneMigrationDataTemporary'],
    tierRecommendation: () => [...subscriptions.keys.root(), 'externalGetTierRecommendation'],

    // Common invalidation keys used by mutations
    invalidationKeys: () => [
      subscriptions.keys.pricingInfo(),
      subscriptions.keys.squareOneSubscriptionInfo(),
      subscriptions.keys.trials(),
      subscriptions.keys.uiAssets(),
      subscriptions.keys.mergedSubscriptions(),
      subscriptions.keys.featureStatuses(),
      subscriptions.keys.planGroup(),
      subscriptions.keys.tierRecommendation(),
      subscriptions.keys.squareOneMigrationFeatures(),
      featurama.keys.bulkRetrieveFeatureStatuses(),
      featurama.keys.bulkRetrieveFeatureSetStatuses(),
      feePlans.keys.getProcessingFees(),
      saasBff.keys.subscriptionSummary(),
    ],

    // Extended invalidation keys for Square One migration decision (includes eligibility)
    migrationInvalidationKeys: () => [
      ...subscriptions.keys.invalidationKeys(),
      subscriptions.keys.squareOneEligibility(),
      subscriptions.keys.squareOneMigrationDataTemporary(),
    ],
  },

  // Methods
  externalListMergedSubscriptions: () => queryOptions({
    queryKey: subscriptions.keys.mergedSubscriptions(),
    queryFn: async () => {
      const { externalListMergedSubscriptions } = await import(
        '../api/subscriptions/rpc'
      );

      return externalListMergedSubscriptions();
    },
  }),
  externalListFeatureStatuses: () => queryOptions({
    queryKey: subscriptions.keys.featureStatuses(),
    queryFn: async () => {
      const { externalListFeatureStatuses } = await import(
        '../api/subscriptions/rpc'
      );

      return externalListFeatureStatuses();
    },
  }),
  externalGetBillerLocationToken: (params: ExternalGetBillerLocationTokenParams) => queryOptions({
    queryKey: [...subscriptions.keys.billerLocationToken(), { params }],
    queryFn: async () => {
      const { externalGetBillerLocationToken } = await import(
        '../api/subscriptions/rpc'
      );

      return externalGetBillerLocationToken(params);
    },
  }),
  externalGetSquareOneEligibility: () => queryOptions({
    queryKey: subscriptions.keys.squareOneEligibility(),
    queryFn: async () => {
      const { externalGetSquareOneEligibility } = await import(
        '../api/subscriptions/rpc'
      );
      return externalGetSquareOneEligibility();
    },
    staleTime: Number.POSITIVE_INFINITY,
  }),
  externalGetPricingInfo: (params: ExternalGetPricingInfoParams) => queryOptions({
    queryKey: [...subscriptions.keys.pricingInfo(), { params }],
    queryFn: async () => {
      const { externalGetPricingInfo } = await import(
        '../api/subscriptions/rpc'
      );
      return externalGetPricingInfo(params);
    },
    staleTime: Number.POSITIVE_INFINITY,
  }),
  externalListTrials: (params: ExternalListTrialsParams) => queryOptions({
    queryKey: [...subscriptions.keys.trials(), { params }],
    queryFn: async () => {
      const { externalListTrials } = await import(
        '../api/subscriptions/rpc'
      );
      return externalListTrials(params);
    },
    staleTime: 60 * 60 * 1000, // 1 hour
  }),
  externalGetPlanGroup: (params: ExternalGetPlanGroupParams) => queryOptions({
    queryKey: [...subscriptions.keys.planGroup(), { params }],
    queryFn: async () => {
      const { externalGetPlanGroup } = await import(
        '../api/subscriptions/rpc'
      );
      return externalGetPlanGroup(params);
    },
    staleTime: Number.POSITIVE_INFINITY,
  }),
  externalGetSquareOneSubscriptionInfo: (params: ExternalGetSquareOneSubscriptionInfoParams = {}) => queryOptions({
    queryKey: [...subscriptions.keys.squareOneSubscriptionInfo(), { params }],
    queryFn: async () => {
      const { externalGetSquareOneSubscriptionInfo } = await import(
        '../api/subscriptions/rpc'
      );
      return externalGetSquareOneSubscriptionInfo(params);
    },
    staleTime: Number.POSITIVE_INFINITY,
  }),
  externalGetUiAssets: (params: ExternalGetUiAssetsParams) => queryOptions({
    queryKey: [...subscriptions.keys.uiAssets(), { params }],
    queryFn: async () => {
      const { externalGetUiAssets } = await import(
        '../api/subscriptions/rpc'
      );
      return externalGetUiAssets(params);
    },
    staleTime: Number.POSITIVE_INFINITY,
  }),

  externalGetSquareOneMigrationFeatures: (params: ExternalGetSquareOneMigrationFeaturesParams) => queryOptions({
    queryKey: [...subscriptions.keys.squareOneMigrationFeatures(), { params }],
    queryFn: async () => {
      const { externalGetSquareOneMigrationFeatures } = await import(
        '../api/subscriptions/rpc'
      );
      return externalGetSquareOneMigrationFeatures(params);
    },
    staleTime: Number.POSITIVE_INFINITY,
  }),
  externalGetSquareOneLostFeatures: (params: ExternalGetSquareOneLostFeaturesParams) => ({
    queryKey: [...subscriptions.keys.squareOneLostFeatures(), { params }],
    queryFn: async () => {
      const { externalGetSquareOneLostFeatures } = await import('../api/subscriptions/rpc');
      return externalGetSquareOneLostFeatures(params);
    },
    staleTime: Number.POSITIVE_INFINITY,
  }),

  externalGetTierRecommendation: (params: ExternalGetTierRecommendationParams) => queryOptions({
    queryKey: [...subscriptions.keys.tierRecommendation(), { params }],
    queryFn: async () => {
      const { externalGetTierRecommendation } = await import(
        '../api/subscriptions/rpc'
      );
      return externalGetTierRecommendation(params);
    },
    staleTime: Number.POSITIVE_INFINITY,
  }),

  externalCreateOrUpdateSubscription: () => ({
    mutationKey: [...subscriptions.keys.root(), 'externalCreateOrUpdateSubscription'],
    async mutationFn(params: ExternalCreateOrUpdateSubscriptionParams) {
      const { externalCreateOrUpdateSubscription } = await import('../api/subscriptions/rpc');
      return await externalCreateOrUpdateSubscription(params);
    },
    meta: {
      invalidates: subscriptions.keys.invalidationKeys(),
    },
  }),
  externalUpdateSubscription: () => ({
    mutationKey: [...subscriptions.keys.root(), 'externalUpdateSubscription'],
    async mutationFn(params: ExternalUpdateSubscriptionParams) {
      const { externalUpdateSubscription } = await import('../api/subscriptions/rpc');
      return await externalUpdateSubscription(params);
    },
    meta: {
      invalidates: subscriptions.keys.invalidationKeys(),
    },
  }),
  externalSetSquareOneMigrationDecision: () => ({
    mutationKey: [...subscriptions.keys.root(), 'externalSetSquareOneMigrationDecision'],
    async mutationFn(params: ExternalSetSquareOneMigrationDecisionParams) {
      const { externalSetSquareOneMigrationDecision } = await import('../api/subscriptions/rpc');
      return await externalSetSquareOneMigrationDecision(params);
    },
    meta: {
      invalidates: subscriptions.keys.migrationInvalidationKeys(),
    },
  }),
  externalMigrateToSquareOne: () => ({
    mutationKey: [...subscriptions.keys.root(), 'externalMigrateToSquareOne'],
    async mutationFn(params: ExternalMigrateToSquareOneParams) {
      const { externalMigrateToSquareOne } = await import('../api/subscriptions/rpc');
      return await externalMigrateToSquareOne(params);
    },
    meta: {
      invalidates: [
        ...subscriptions.keys.migrationInvalidationKeys(),
      ],
    },
  }),
  fetchSquareOneMigrationData: () => queryOptions({
    queryKey: subscriptions.keys.squareOneMigrationDataTemporary(),
    queryFn: async () => {
      const { externalGetSquareOneEligibility } = await import(
        '../api/subscriptions/rpc'
      );
      return externalGetSquareOneEligibility();
    },
    staleTime: Number.POSITIVE_INFINITY,
  }),
};
